import { isPlainObject } from './isPlainObject';

/**
 * Single source of truth: the blocks, the import coercions and the admin controls are all keyed off
 * the union below, so a new type fails to compile until every side handles it.
 */
export const SCALAR_FIELD_LABELS = {
  'text-field': 'Text Field',
  'textarea-field': 'Textarea Field',
  'number-field': 'Number Field',
  'checkbox-field': 'Checkbox Field',
  'date-field': 'Date Field',
  'select-field': 'Select Field',
} as const;

export const NESTED_FIELD_LABELS = {
  'group-field': 'Group Field',
  'array-field': 'Array Field',
} as const;

export const MAX_DEFINITION_DEPTH = 4;

const DYNAMIC_FIELD_LABELS = { ...SCALAR_FIELD_LABELS, ...NESTED_FIELD_LABELS } as const;

export type ScalarFieldType = keyof typeof SCALAR_FIELD_LABELS;
export type NestedFieldType = keyof typeof NESTED_FIELD_LABELS;
export type DynamicFieldType = keyof typeof DYNAMIC_FIELD_LABELS;

const DEFAULT_DYNAMIC_FIELD_TYPE: DynamicFieldType = 'text-field';

export const asDynamicFieldType = (blockType: string): DynamicFieldType =>
  blockType in DYNAMIC_FIELD_LABELS ? (blockType as DynamicFieldType) : DEFAULT_DYNAMIC_FIELD_TYPE;

export const isNestedFieldType = (blockType: string): blockType is NestedFieldType => blockType in NESTED_FIELD_LABELS;

/**
 * `findArrayOrphans` looks for a string `id` on each item, so this name is what makes the MCP orphan
 * guard protect these rows. Payload adds no id of its own inside a JSON field.
 *
 * A curator must never declare a field with this name: the value would overwrite the row identity.
 * {@link RESERVED_FIELD_NAMES} refuses it.
 */
export const ROW_ID_KEY = 'id';

/** Names a curator cannot use for a field, because the row identity already owns them. */
export const RESERVED_FIELD_NAMES: string[] = [ROW_ID_KEY];

export const newRowId = (): string => crypto.randomUUID?.() ?? `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface DynamicFieldDefinition {
  blockType: string;
  name: string;
  label: string;
  required?: boolean;
  options?: { label: string; value: string }[];
  /** Child definitions. Set on a group and on an array field, absent on a scalar type. */
  fields?: DynamicFieldDefinition[];
}

/**
 * A relationship as the Local API returns it: an id string, a number, or a populated row. Raw-driver
 * shapes (ObjectId, `_id`) are `asIdString` in the OAuth plugin — do not merge the two, or every
 * access gate that calls this starts accepting shapes Payload never hands it.
 */
export const relationshipId = (value: unknown): string | undefined => {
  if (!value) return;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (isPlainObject(value) && value.id != null) return String(value.id);
};

export const fetchFieldDefinitions = async (typeId: string): Promise<DynamicFieldDefinition[]> => {
  const response = await fetch(`/api/collection-config/${typeId}?depth=0`, { credentials: 'include' });
  if (!response.ok) throw new Error(`Could not load field definitions (${response.status})`);
  const config = await response.json();
  // A never-published config has no fields to hand out, but returning [] is
  // indistinguishable from "published with no fields" — which let an importer report a
  // cheerful "0 fields pre-filled" instead of the real reason. Both callers catch.
  if (config?._status !== 'published') throw new Error('This Collection Config is not published yet, so it has no fields');

  return Array.isArray(config?.dynamicFields) ? config.dynamicFields : [];
};

export const arrayRows = (value: unknown): Record<string, unknown>[] => (Array.isArray(value) ? value : []).filter(isPlainObject);

export const groupValue = (value: unknown): Record<string, unknown> => (isPlainObject(value) ? value : {});

export const isDynamicValueEmpty = (definition: DynamicFieldDefinition, value: unknown): boolean => {
  if (definition.blockType === 'checkbox-field') return !value;

  if (definition.blockType === 'group-field') {
    const object = groupValue(value);

    return (definition.fields ?? []).every((child) => isDynamicValueEmpty(child, object[child.name]));
  }

  if (definition.blockType === 'array-field') return arrayRows(value).length === 0;

  return value == null || value === '';
};

const qualify = (parent: string | undefined, label: string): string => (parent ? `${parent} → ${label}` : label);

/**
 * The labels of every required field left empty. Qualified through the nesting, so a missing amount
 * in the second price row reads "Prices 2 → Amount".
 *
 * A group's children are demanded whether or not the group holds anything, which is how Payload's own
 * group behaves: a group always exists, so a required child of one is always required. A row of an
 * array field is different, because a row only exists once a curator adds it.
 */
export const missingRequiredLabels = (definitions: DynamicFieldDefinition[], values: Record<string, unknown>, parent?: string): string[] =>
  definitions.flatMap((definition) => {
    const label = qualify(parent, definition.label || definition.name);
    const value = values[definition.name];
    const empty = isDynamicValueEmpty(definition, value);

    if (definition.blockType === 'group-field') {
      const children = missingRequiredLabels(definition.fields ?? [], groupValue(value), label);

      // Naming a missing child is more use than naming the group that holds it.
      if (children.length > 0) return children;

      return empty && definition.required ? [label] : [];
    }

    if (empty) return definition.required ? [label] : [];

    if (definition.blockType === 'array-field') {
      return arrayRows(value).flatMap((row, index) => missingRequiredLabels(definition.fields ?? [], row, `${label} ${index + 1}`));
    }

    return [];
  });

/**
 * The React key for one array row. A row id and a row position must never share a key space: a row
 * with no id would otherwise take a key an identified row already holds, and removing a row would
 * move one row's open state or cursor onto another.
 */
export const arrayRowKey = (row: Record<string, unknown>, index: number): string => {
  const id = row[ROW_ID_KEY];

  return typeof id === 'string' && id ? `id:${id}` : `index:${index}`;
};

/** Row edits, kept out of the admin control so they can be tested without a DOM. */
export const addArrayRow = (value: unknown): Record<string, unknown>[] => [...arrayRows(value), { [ROW_ID_KEY]: newRowId() }];

export const removeArrayRow = (value: unknown, index: number): Record<string, unknown>[] =>
  arrayRows(value).filter((_, position) => position !== index);

export const setArrayRowValue = (value: unknown, index: number, name: string, next: unknown): Record<string, unknown>[] =>
  arrayRows(value).map((row, position) => (position === index ? { ...row, [name]: next } : row));
